// AboutTechVentureLLC.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaLaptopCode, FaHandshake, FaMapMarkerAlt } from "react-icons/fa";

// Images (update paths as needed)
import card1 from "../../images/img/card1.png";
import card2 from "../../images/img/card2.png";
import card3 from "../../images/img/card3.png";

export default function AboutTechVentureLLC() {
  useEffect(() => {
    document.title = "About TechVenture LLC — Learn, Practice, Get Recognized";
  }, []);

  const stats = [
    { k: "Founded", v: "2022" },
    { k: "Learners", v: "25K+" },
    { k: "Interview Qs", v: "9K+" },
    { k: "Certificates", v: "30+" },
  ];

  const values = [
    {
      Icon: FaLaptopCode,
      color: "#2563eb",
      title: "Who we are",
      text: "Engineers first. We prioritize correctness, performance, accessibility, and maintainability.",
    },
    {
      Icon: FaHandshake,
      color: "#0ea5e9",
      title: "What we live by",
      text: "Clear SLAs, thoughtful communication, and reliable outcomes. We’re partners—not just vendors.",
    },
    {
      Icon: FaMapMarkerAlt,
      color: "#6d28d9",
      title: "Where we’re located",
      text: "Based in Sterling, Virginia—near Washington, D.C.—serving teams across the globe.",
    },
  ];

  const services = [
    {
      img: card1,
      title: "Web Development",
      desc:
        "Modern, secure web apps—front-end through back-end—with performance, accessibility, and maintainability baked in.",
      bullets: ["React / Angular / TypeScript", "Spring Boot / Node.js APIs", "CI/CD • Testing • Observability"],
    },
    {
      img: card2,
      title: "E-Commerce",
      desc:
        "Scalable storefronts and integrations that convert—from catalog and checkout to analytics and loyalty.",
      bullets: ["Headless commerce", "Payments & tax integrations", "SEO & Core Web Vitals"],
    },
    {
      img: card3,
      title: "Cybersecurity",
      desc:
        "Secure by design. Threat modeling, least-privilege, and automated checks to protect code, data, and users.",
      bullets: ["AppSec reviews", "Secrets & identity (OAuth/OIDC)", "WAF/CDN hardening"],
    },
  ];

  return (
    <div className="container-fluid p-0" role="main">
      {/* Optional SEO via react-helmet-async can go here */}

      {/* Hero */}
      <section
        className="py-5 text-white text-center"
        style={{
          background:
            "linear-gradient(135deg, #0d6efd 0%, #6610f2 45%, #6f42c1 100%)",
        }}
      >
        <div className="container">
          <span className="badge bg-light text-dark mb-3">About TechVenture LLC</span>
          <h1 className="display-6 fw-bold mb-2">Learn. Practice. Get Recognized.</h1>
          <p className="lead mb-0 opacity-90">
            Since 2022, we’ve helped developers and teams master the stack, prepare for interviews,
            and earn shareable certificates.
          </p>
        </div>
      </section>

      {/* Mission + Snapshot */}
      <section className="container py-5">
        <div className="row g-4 align-items-center">
          <div className="col-lg-7">
            <h2 className="h3 mb-3">Our mission</h2>
            <p className="mb-3">
              TechVenture LLC is an IT consulting and learning platform focused on practical,
              high-quality engineering. We mix curated content with real-world experience so you
              can build secure, observable, and scalable systems—faster.
            </p>
            <div className="row text-center mt-3 g-3">
              {stats.map((s) => (
                <div className="col-6 col-md-3" key={s.k}>
                  <div className="fw-bold fs-4">{s.v}</div>
                  <div className="text-muted small">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h3 className="h5">What we provide</h3>
                <ul className="mb-0">
                  <li>Career-ready learning paths & interview prep</li>
                  <li>Hands-on labs with DevOps & cloud tooling</li>
                  <li>System Design & Coding practice sets</li>
                  <li>Verified badges & certificates</li>
                  <li>Consulting for small & mid-size teams</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values (Who / What / Where) */}
      <section className="container pb-2">
        <div className="row row-cols-1 row-cols-md-3 g-4">
          {values.map((v) => (
            <div className="col" key={v.title}>
              <div className="card h-100 shadow-sm border-0">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ aspectRatio: "16 / 9", background: `${v.color}14` }}
                >
                  <v.Icon size={40} color={v.color} aria-hidden="true" />
                </div>
                <div className="card-body">
                  <h3 className="h5 card-title">{v.title}</h3>
                  <p className="card-text mb-0">{v.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="container py-5 border-top">
        <h2 className="h4 text-center mb-4">Services</h2>
        <div className="row g-4">
          {services.map((s) => (
            <div className="col-12 col-md-6 col-lg-4" key={s.title}>
              <div className="card h-100 shadow-sm border-0">
                <div className="text-center pt-3">
                  <img
                    src={s.img}
                    alt={`${s.title} illustration`}
                    className="img-fluid"
                    style={{ maxHeight: 140 }}
                  />
                </div>
                <div className="card-body">
                  <h3 className="h5 card-title text-center">{s.title}</h3>
                  <p className="card-text">{s.desc}</p>
                  {s.bullets?.length ? (
                    <ul className="mb-0">
                      {s.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Narrative */}
      <section className="container py-4">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-secondary" role="note">
              Since 2022, TechVenture LLC has delivered full-featured consulting and hosting
              services to small and mid-size businesses. We help you get online fast and keep your
              systems secure, observable, and scalable—from day one.
            </div>
            <p className="mb-0">
              Our team supports the entire software lifecycle—from discovery and architecture to
              implementation, testing, and hand-off. Thank you for trusting us with your growth.
            </p>
          </div>
        </div>
      </section>

      {/* Practice & Prep (replaces Posts + Pagination) */}
      <section className="container py-5 border-top">
        <div className="row g-4">
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h3 className="h5 mb-2">Practice Coding</h3>
                <p className="text-muted mb-3">
                  Solve curated problems with patterns like Two Pointers, Sliding Window, and more.
                </p>
                <Link to="/codingRound" className="btn btn-outline-primary">
                  Start practicing
                </Link>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h3 className="h5 mb-2">System Design</h3>
                <p className="text-muted mb-3">
                  Deep-dive design prompts with trade-offs, scaling strategies, and diagrams.
                </p>
                <Link to="/systemDesign" className="btn btn-outline-secondary">
                  Explore prompts
                </Link>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h3 className="h5 mb-2">Earn Certificates</h3>
                <p className="text-muted mb-3">
                  Complete learning paths and add verified badges to your LinkedIn profile.
                </p>
                <Link to="/badges" className="btn btn-outline-success">
                  View badges
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white text-center" style={{ background: "var(--tv-chrome-bg)" }}>
        <div className="container">
          <h2 className="fw-bold mb-2">Partner with TechVenture</h2>
          <p className="text-white-50 mb-4">
            We collaborate with teams on training, content, and platform engineering.
          </p>
          <Link to="/contactUs" className="btn btn-primary btn-lg">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
